import React from "react";

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  PermissionGuard,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components";
import { FloatingLabelInput } from "@/components/ui/floating-label-input";
import { useI18n } from "@/context";

import { usePartnerPortalCard } from "./partner-portal-card.hooks";

interface IPartnerPortalCardProps {
  organizationId: string;
}

export const PartnerPortalCard: React.FC<IPartnerPortalCardProps> = ({
  organizationId,
}) => {
  const { t } = useI18n();
  const {
    form,
    onSubmit,
    masterExists,
    isLoadingExists,
    isSubmitting,
  } = usePartnerPortalCard(organizationId);

  const useRepresentative = form.watch("use_representative_data");

  React.useEffect(() => {
    if (useRepresentative) {
      form.setValue("manual", undefined);
      form.clearErrors("manual");
      return;
    }
    if (!form.getValues("manual")) {
      form.setValue("manual", {
        first_name: "",
        last_name: "",
        document: "",
        document_type: "cpf",
        phone: "",
        birth_date: "",
        address: {
          street: "",
          number: "",
          complement: "",
          neighborhood: "",
          city: "",
          state: "",
          country: "Brasil",
          postal_code: "",
        },
      });
    }
  }, [useRepresentative, form]);

  return (
    <PermissionGuard permission="partners:edit">
      <Card>
        <CardHeader>
          <CardTitle>{t("clients.partnerPortal.title")}</CardTitle>
          <p className="text-sm text-muted-foreground">
            {t("clients.partnerPortal.description")}
          </p>
        </CardHeader>
        <CardContent>
          {isLoadingExists ? (
            <p className="text-sm text-muted-foreground">{t("generic.loading")}</p>
          ) : masterExists ? (
            <p className="text-sm text-muted-foreground">
              {t("clients.partnerPortal.masterExists")}
            </p>
          ) : (
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-4 max-w-lg"
              >
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <FloatingLabelInput
                          label={t("clients.partnerPortal.email")}
                          type="email"
                          autoComplete="email"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <FloatingLabelInput
                          label={t("clients.partnerPortal.password")}
                          type="password"
                          autoComplete="new-password"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <FloatingLabelInput
                          label={t("clients.partnerPortal.confirmPassword")}
                          type="password"
                          autoComplete="new-password"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="use_representative_data"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={(v) =>
                            field.onChange(Boolean(v === true))
                          }
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>
                          {t("clients.partnerPortal.useRepresentative")}
                        </FormLabel>
                      </div>
                    </FormItem>
                  )}
                />

                {!useRepresentative && (
                  <div className="space-y-4 pt-2 border-t border-border">
                    <p className="text-sm font-medium">
                      {t("clients.partnerPortal.manualSection")}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="manual.first_name"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <FloatingLabelInput
                                label={t("users.fields.firstName")}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="manual.last_name"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <FloatingLabelInput
                                label={t("users.fields.lastName")}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <FormField
                      control={form.control}
                      name="manual.document_type"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("users.fields.documentType")}</FormLabel>
                          <FormControl>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value || ""}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectGroup>
                                  <SelectItem value="cpf">CPF</SelectItem>
                                  <SelectItem value="cnpj">CNPJ</SelectItem>
                                </SelectGroup>
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="manual.document"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <FloatingLabelInput
                              label={t("users.fields.document")}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="manual.phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <FloatingLabelInput
                              label={t("users.fields.phone")}
                              type="tel"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="manual.birth_date"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <FloatingLabelInput
                              label={t("users.fields.birthDate")}
                              placeholder="YYYY-MM-DD"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <p className="text-sm font-medium">{t("clients.review.address")}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="manual.address.postal_code"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <FloatingLabelInput
                                label={t("users.fields.zipcode")}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="manual.address.street"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <FloatingLabelInput
                                label={t("users.fields.street")}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="manual.address.number"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <FloatingLabelInput
                                label={t("users.fields.number")}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="manual.address.complement"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <FloatingLabelInput
                                label={t("users.fields.complement")}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="manual.address.neighborhood"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <FloatingLabelInput
                                label={t("users.fields.neighborhood")}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="manual.address.city"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <FloatingLabelInput
                                label={t("users.fields.city")}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="manual.address.state"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <FloatingLabelInput
                                label={t("users.fields.state")}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                )}

                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {t("clients.partnerPortal.submit")}
                </Button>
              </form>
            </Form>
          )}
        </CardContent>
      </Card>
    </PermissionGuard>
  );
};
